import memoizeOne from "memoize-one";
import type {IconName} from "@cdx/ds";
import type {Deck, DeckId} from "../../cdx-models/Deck";
import type {Card} from "../../cdx-models/Card";
import {hasChildCards} from "../../features/workflows/workflow-utils";
import type {Root} from "../../cdx-models/Root";
import type {UserId} from "../../cdx-models/User";
import type {DeckSubscription} from "../../cdx-models/DeckSubscription";
import {hasScope} from "../../lib/auth/scopes";
import {getUserRole} from "../../lib/auth/roles";

const emptySet = new Set();

const getSubMap = memoizeOne((subs: DeckSubscription[]) => {
  if (subs.length && !subs[0].$meta.isLoaded) return emptySet;
  return new Set(subs.map((s) => s.deck?.id));
});

export const isUserSubscribedToDeck = ({
  root,
  deckId,
  userId,
}: {
  root: Root;
  deckId: DeckId | null;
  userId: UserId | null;
}) => {
  if (!userId || !deckId) return false;
  const subs = root.account.$meta.find("deckSubscriptions", {userId});
  const map = getSubMap(subs);
  return map.has(deckId);
};

export const getDeckFilterFnForCard = ({isNewCard, card}: {isNewCard?: boolean; card: Card}) => {
  if (card.isDoc) {
    return () => true;
  } else if (isNewCard) {
    return (deck: Deck) => (card.isDoc ? true : deck.deckType !== "doc");
  } else {
    const withChildren = hasChildCards(card);
    if (withChildren) {
      if (card.isDoc) {
        return (deck: Deck) => deck.deckType !== "task";
      } else {
        return (deck: Deck) => deck.deckType === "mixed" || deck.deckType === "hero";
      }
    } else if (card.parentCard) {
      return (deck: Deck) => deck.deckType === "task" || deck.deckType === "mixed";
    } else {
      return (deck: Deck) => deck.deckType !== "doc";
    }
  }
};

export const deckTypeToIconName: {[K in Deck["deckType"]]: IconName} = {
  hero: "Crown",
  doc: "Book",
  task: "CardTriplet",
  mixed: "Misc",
};

/** The server's `ensureGuardianRule`: a guardian, or `project:write` in the deck's project. */
export const passesGuardianRule = (root: Root, card: Card) => {
  const {isLoaded, userId} = getUserRole(root);
  if (!isLoaded || !userId) return false;
  if (!card.deck || !card.deck.hasGuardians) return true;
  if (hasScope(root, "project:write", {project: card.deck.project})) return true;
  return card.deck.guardians.some((g) => g.$meta.getId("user") === userId);
};

/**
 * The server sets a done or archived hero card back to a visible, not started one when a sub card is
 * added to it or restored.
 */
const isReopenedBySubCard = (parentCard: Card) =>
  parentCard.status === "done" || parentCard.visibility === "archived";

export const passesGuardianRuleForParent = (root: Root, parentCard: Card | null) => {
  if (!parentCard || !isReopenedBySubCard(parentCard)) return true;
  return passesGuardianRule(root, parentCard);
};

/**
 * The cards failing `passesGuardianRule` for a visibility change, including what the server changes
 * along with it:
 * - archiving or deleting a hero card does the same to its sub cards
 * - restoring a sub card restores its hero card
 */
export const getGuardedCardsForVisibility = (
  root: Root,
  card: Card,
  nextVisibility: "default" | "archived" | "deleted"
): Card[] => {
  if (card.visibility === nextVisibility) return [];
  const getAffectedCards = () => {
    switch (nextVisibility) {
      case "archived":
      case "deleted":
        return [card, ...card.childCards.filter((child) => child.visibility !== nextVisibility)];
      case "default": {
        const parent = card.parentCard;
        return parent && isReopenedBySubCard(parent) ? [card, parent] : [card];
      }
    }
  };
  return getAffectedCards().filter((c) => !passesGuardianRule(root, c));
};

export const passesGuardianRuleForVisibility = (
  root: Root,
  card: Card,
  nextVisibility: "default" | "archived" | "deleted"
) => getGuardedCardsForVisibility(root, card, nextVisibility).length === 0;
